/**
 * A protected file — a chat photo, a template's header image — as an address an
 * `<img>` or `<video>` can show.
 *
 * Such a file is served only by the API, behind the signed-in session's token,
 * so its path cannot go in `src` directly: it is fetched with the token and
 * shown from an object URL. The URL is released when the query is dropped from
 * the cache, not when one viewer unmounts — another may still be showing it.
 */

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api-client';

export function fileUrlKey(path: string) {
  return ['protected-file', path] as const;
}

export function useFileUrl(path: string | null, { enabled = true }: { enabled?: boolean } = {}) {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Release object URLs as the cache lets their queries go.
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'removed') return;
      const [kind] = event.query.queryKey;
      const url = event.query.state.data;
      if (kind === 'protected-file' && typeof url === 'string') URL.revokeObjectURL(url);
    });
  }, [queryClient]);

  return useQuery({
    queryKey: fileUrlKey(path ?? ''),
    enabled: enabled && path !== null,
    staleTime: Infinity,
    gcTime: 10 * 60_000,
    queryFn: async () => URL.createObjectURL((await api.file(path ?? '')).blob),
  });
}
